import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, LayoutGrid } from 'lucide-react';
import { ProgressBar } from './components/ui/ProgressBar';
import { SlideTitle } from './components/SlideTitle';
import { SlideLifecycle } from './components/SlideLifecycle';
import { SlideMarketSize } from './components/SlideMarketSize';
import { SlideProblem } from './components/SlideProblem';
import { SlideAIOpportunity } from './components/SlideAIOpportunity';
import { SlideCalculator } from './components/SlideCalculator';
import { SlideAIEconomics } from './components/SlideAIEconomics';
import { SlideNewCategory } from './components/SlideNewCategory';
import { SlideStrategy } from './components/SlideStrategy';
import { SlideWhatSnakkerDoes } from './components/SlideWhatSnakkerDoes';
import { SlideBusinessModel } from './components/SlideBusinessModel';
import { SlideCampaignExample } from './components/SlideCampaignExample';
import { SlidePlatformArchitecture } from './components/SlidePlatformArchitecture';
import { SlideMoat } from './components/SlideMoat';
import { SlideTraction } from './components/SlideTraction';
import { SlideSalesFocus } from './components/SlideSalesFocus';
import { SlideFastCalculator } from './components/SlideFastCalculator';
import { SlideFinancialProjections } from './components/SlideFinancialProjections';
import { SlideCTA } from './components/SlideCTA';
import { ExecutiveSummary } from './components/ExecutiveSummary';
import { Storyboard } from './components/Storyboard';
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [storyboardOpen, setStoryboardOpen] = useState(false);
  const totalSlides = 18;
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlide = () => {
    switch (currentSlide) {
      case 0:
        return <SlideTitle onContinue={nextSlide} data-id="element-0" data-mp="2suqz5ptjhzox" />;
      case 1:
        return <SlideLifecycle onContinue={nextSlide} data-id="element-1" data-mp="18e1ajmuczsl3" />;
      case 2:
        return <SlideMarketSize onContinue={nextSlide} data-id="element-2" data-mp="18boj8j2po5vd" />;
      case 3:
        return <SlideProblem onContinue={nextSlide} data-id="element-3" data-mp="1d59linbc7m0d" />;
      case 4:
        return <SlideCalculator onContinue={nextSlide} data-id="element-4" data-mp="yg3mp0gevxt0" />;
      case 5:
        return <SlideAIEconomics onContinue={nextSlide} data-id="element-5" data-mp="ydqxc28bql5x" />;
      case 6:
        return <SlidePlatformArchitecture onContinue={nextSlide} data-id="element-6" data-mp="13f7sri81hff4" />;
      case 7:
        return <SlideNewCategory onContinue={nextSlide} data-id="element-7" data-mp="1r5ai3qkkk7a1" />;
      case 8:
        return <SlideAIOpportunity onContinue={nextSlide} data-id="element-8" data-mp="1w3vn03arnf4t" />;
      case 9:
        return <SlideStrategy onContinue={nextSlide} data-id="element-9" data-mp="1w0zniaj9sie9" />;
      case 10:
        return <SlideBusinessModel onContinue={nextSlide} data-id="element-10" data-mp="1i31e7jjt3c5m" />;
      case 11:
        return <SlideWhatSnakkerDoes onContinue={nextSlide} data-id="element-11" data-mp="1m44s40iamy47" />;
      case 12:
        return <SlideCampaignExample onContinue={nextSlide} data-id="element-12" data-mp="6nw7m4gk0tgq" />;
      case 13:
        return <SlideMoat onContinue={nextSlide} data-id="element-13" data-mp="aq0y4ubph9z5" />;
      case 14:
        return <SlideSalesFocus onContinue={nextSlide} data-id="element-14" data-mp="arke0jtg9j0e" />;
      case 15:
        return <SlideFastCalculator onContinue={nextSlide} data-id="element-15" data-mp="qy74n4km57a5" />;
      case 16:
        return <SlideFinancialProjections onContinue={nextSlide} data-id="element-16" data-mp="m6p24c2xyyn2" />;
      case 17:
        return <SlideCTA onContinue={nextSlide} data-id="element-17" data-mp="h7c54yicwb6r" />;
      default:
        return null;
    }
  };
  return <div className="w-full h-screen bg-navy-800 overflow-hidden relative font-sans text-slate-900" data-id="element-18" data-mp="h4znkmgig2u8">
      <ProgressBar current={currentSlide} total={totalSlides} onBack={prevSlide} onNext={nextSlide} data-id="element-19" data-mp="c6ermz8ak5bl" />

      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-20" data-mp="14xydkqw9vxha">
        <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-21" data-mp="zxk372778bg3">
          {renderSlide()}
        </motion.div>
      </AnimatePresence>

      <div className="fixed bottom-6 left-6 z-40 flex items-center gap-2" data-id="element-22" data-mp="206timxlxzt9l">
      <button onClick={() => setSummaryOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#6C2BD9] text-sm font-semibold shadow-lg shadow-[#6C2BD9]/30 hover:bg-[#5A23B8] hover:shadow-xl hover:shadow-[#6C2BD9]/40 hover:-translate-y-0.5 transition-all" aria-label="Open executive summary" style={{
        color: '#ffffff'
      }} data-id="element-23" data-mp="254v23jna5ull">
        <Sparkles className="w-4 h-4" style={{
          color: '#ffffff'
        }} data-id="element-24" data-mp="2nvcufn47ao6f" />
        <span style={{
          color: '#ffffff'
        }} data-id="element-25" data-mp="2steeobdd4tjh">
          Executive Summary
        </span>
      </button>

        <button onClick={() => setStoryboardOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 shadow-lg hover:bg-slate-50 hover:-translate-y-0.5 transition-all" aria-label="Open visual storyboard" data-id="element-26" data-mp="ygoj7p7cjc9z">
          <LayoutGrid className="w-4 h-4 text-[#6C2BD9]" data-id="element-27" data-mp="2asileayoejtt" />
          <span data-id="element-28" data-mp="2apw59mxpyvzz">Storyboard</span>
        </button>
      </div>

      <ExecutiveSummary open={summaryOpen} onClose={() => setSummaryOpen(false)} data-id="element-29" data-mp="25se62o9i7v56" />

      <Storyboard open={storyboardOpen} onClose={() => setStoryboardOpen(false)} data-id="element-30" data-mp="2kovg9c5yj3q5" />
    </div>;
}